import Dropdown from "bootstrap/js/dist/dropdown";
import { Fragment, memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isAuthenticatedSelector, userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import {
	isCountryEgypt,
	isLaboratoryAndImagingSegment,
	isMedicalSegment,
	isPatientSegment,
	isPharmacySegment,
} from "utils/helpers";
import LocaleNavLink from "views/components/LocaleNavLink";

const RootHomeNav = ({ isVertical, fromOffcanvas }) => {
	const { t } = useTranslation();

	// redux global state
	const globals = useSelector(globalsSelector);
	const user = useSelector(userSelector);
	const segments = useSelector(segmentsSelector);
	const isAuthenticated = useSelector(isAuthenticatedSelector);

	// ref hook
	const demoDropdownRef = useRef(null);
	const segmentsDropdownRef = useRef(null);

	// constants
	const isEgyptianGuest =
		(isAuthenticated && isCountryEgypt(user?.country)) ||
		(!isAuthenticated && globals?.client_location?.is_egypt) ||
		false;
	const filteredSegments = segments?.filter(
		(segment) =>
			(!isEgyptianGuest || (isEgyptianGuest && !isPharmacySegment(segment))) && !isPatientSegment(segment)
	);
	const filteredSegmentsDemo = filteredSegments?.filter(({ demo }) => Boolean(demo?.domain || ""));

	// effect hook
	useEffect(() => {
		const demoDropdownRefCurrent = demoDropdownRef?.current;
		let demoDropdown;
		if (!fromOffcanvas && segments?.filter(({ demo }) => demo?.domain)?.length && !isAuthenticated) {
			demoDropdown = Dropdown.getInstance(demoDropdownRefCurrent);
			if (!demoDropdown) demoDropdown = new Dropdown(demoDropdownRefCurrent);

			// open dropdown on hover
			demoDropdownRefCurrent.addEventListener("mouseenter", ({ target }) => {
				demoDropdown.show();
				target.blur();
			});
			demoDropdownRefCurrent.addEventListener("mouseleave", ({ target }) => {
				const demoDropdownMenu = target.closest(".dropdown").querySelector(".dropdown-menu");
				const t = setTimeout(() => demoDropdown.hide(), 100);

				demoDropdownMenu.addEventListener("mouseenter", () => {
					demoDropdown.show();
					clearTimeout(t);
				});
				demoDropdownMenu.addEventListener("mouseleave", () => {
					demoDropdown.hide();
				});
			});
		}

		return () =>
			!fromOffcanvas && demoDropdown && !isAuthenticated
				? Dropdown.getInstance(demoDropdownRefCurrent).dispose()
				: false;
	}, [fromOffcanvas, segments, isAuthenticated]);

	useEffect(() => {
		const segmentsDropdownRefCurrent = segmentsDropdownRef?.current;
		let segmentsDropdown;
		if (!fromOffcanvas) {
			segmentsDropdown = Dropdown.getInstance(segmentsDropdownRefCurrent);
			if (!segmentsDropdown) segmentsDropdown = new Dropdown(segmentsDropdownRefCurrent);

			// open dropdown on hover
			segmentsDropdownRefCurrent.addEventListener("mouseenter", ({ target }) => {
				segmentsDropdown.show();
				target.blur();
			});
			segmentsDropdownRefCurrent.addEventListener("mouseleave", ({ target }) => {
				const segmentsDropdownMenu = target.closest(".dropdown").querySelector(".dropdown-menu");
				const t = setTimeout(() => segmentsDropdown.hide(), 100);

				segmentsDropdownMenu.addEventListener("mouseenter", () => {
					segmentsDropdown.show();
					clearTimeout(t);
				});
				segmentsDropdownMenu.addEventListener("mouseleave", () => {
					segmentsDropdown.hide();
				});
			});
		}

		return () =>
			!fromOffcanvas && segmentsDropdown ? Dropdown.getInstance(segmentsDropdownRefCurrent).dispose() : false;
	}, [fromOffcanvas]);

	return (
		<nav className="root-home-nav h-100 w-100" aria-label={t("titles.home_navigation")}>
			<ul
				className={`nav row gx-3 gx-lg-2 flex-nowrap align-items-center h-100 w-100 m-0 ${
					isVertical ? "flex-column gy-3" : "justify-content-end"
				}`}>
				<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
					<a
						href="#root-main"
						className={`btn btn-link nav-link nav-link-skip py-2 pe-2 ps-3 border-0 text-uppercase text-decoration-none visually-hidden-focusable rounded-pill ${
							isVertical ? "fs-4" : ""
						}`}>
						<small>
							<strong>{t("actions.skip_to_content")}</strong>
						</small>
					</a>
				</li>
				{!isAuthenticated && (
					<>
						{fromOffcanvas ? (
							<>
								{filteredSegments.map((singleSegment, index) => (
									<li
										className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}
										key={index}>
										<LocaleNavLink
											to={
												isPharmacySegment(singleSegment)
													? "/pharmacists"
													: isMedicalSegment(singleSegment)
													? "/doctors"
													: isLaboratoryAndImagingSegment(singleSegment)
													? "/laboratory-and-imaging"
													: "#"
											}
											className={`btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
												isVertical ? "fs-4" : ""
											}`}>
											<small>
												<strong>{singleSegment?.title || ""}</strong>
											</small>
										</LocaleNavLink>
									</li>
								))}
							</>
						) : (
							<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
								<span className="dropdown h-100">
									<button
										className={`dropdown-toggle dropdown-toggle-with-rotate-icon btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
											isVertical ? "fs-4" : ""
										}`}
										type="button"
										id="root-header-segments-dropdown-toggle"
										data-bs-toggle="dropdown"
										aria-expanded="false"
										ref={segmentsDropdownRef}>
										<span className="d-flex align-items-center flex-nowrap">
											<small className="btn-text text-uppercase small text-start">
												<strong>Taptype For</strong>
											</small>
											<span className="btn-icon ms-1">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="1em"
													height="1em"
													fill="currentColor"
													viewBox="0 0 16 16">
													<path
														fillRule="evenodd"
														d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
													/>
												</svg>
											</span>
										</span>
									</button>
									<ul
										className="dropdown-menu text-bg-yellow w-fit-content text-uppercase rounded-3 shadow"
										aria-labelledby="root-header-segments-dropdown-toggle">
										{filteredSegments.map((singleSegment) => (
											<li key={singleSegment.id}>
												<LocaleNavLink
													to={
														isPharmacySegment(singleSegment)
															? "/pharmacists"
															: isMedicalSegment(singleSegment)
															? "/doctors"
															: isLaboratoryAndImagingSegment(singleSegment)
															? "/laboratory-and-imaging"
															: "#"
													}
													className="dropdown-item text-reset px-4 bg-transparent bg-yellow-200-hover bg-yellow-200-active">
													<small>
														<strong>{singleSegment?.title || ""}</strong>
													</small>
												</LocaleNavLink>
											</li>
										))}
									</ul>
								</span>
							</li>
						)}
						{!isEgyptianGuest && (
							<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
								<LocaleNavLink
									to="/pricing"
									className={`btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
										isVertical ? "fs-4" : ""
									}`}>
									<small>
										<strong>{t("actions.pricing")}</strong>
									</small>
								</LocaleNavLink>
							</li>
						)}
						<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
							<LocaleNavLink
								to="/about-us"
								className={`btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
									isVertical ? "fs-4" : ""
								}`}>
								<small>
									<strong>{t("actions.about_us")}</strong>
								</small>
							</LocaleNavLink>
						</li>
						<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
							<LocaleNavLink
								to="/support/contact-us"
								className={`btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
									isVertical ? "fs-4" : ""
								}`}>
								<small>
									<strong>{t("actions.contacts")}</strong>
								</small>
							</LocaleNavLink>
						</li>
						{filteredSegmentsDemo?.length > 0 &&
							(fromOffcanvas ? (
								filteredSegmentsDemo?.map(({ title = "", demo = {} }) => (
									<li
										className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}
										key={demo?.domain}>
										<a
											href={`${demo.domain}/demo`}
											className={`btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
												isVertical ? "fs-4" : ""
											}`}
											target="_blank"
											rel="noopener noreferrer">
											<small>
												<strong>{`Demo for ${title}`}</strong>
											</small>
										</a>
									</li>
								))
							) : (
								<li className={`nav-item col-auto ${!isVertical ? "d-none d-lg-list-item" : ""}`}>
									<span className="dropdown h-100">
										<button
											className={`dropdown-toggle dropdown-toggle-with-rotate-icon btn btn-link nav-link link-primary-400 w-100 py-2 pe-2 ps-3 border-0 text-decoration-none rounded-pill text-uppercase ${
												isVertical ? "fs-4" : ""
											}`}
											type="button"
											id="root-header-demo-dropdown-toggle"
											data-bs-toggle="dropdown"
											aria-expanded="false"
											ref={demoDropdownRef}>
											<span className="d-flex align-items-center flex-nowrap">
												<small className="btn-text text-uppercase small text-start">
													<strong>Demo</strong>
												</small>
												<span className="btn-icon ms-1">
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="1em"
														height="1em"
														fill="currentColor"
														viewBox="0 0 16 16">
														<path
															fillRule="evenodd"
															d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
														/>
													</svg>
												</span>
											</span>
										</button>
										<ul
											className="dropdown-menu text-bg-yellow w-fit-content text-uppercase rounded-3 shadow"
											aria-labelledby="root-header-demo-dropdown-toggle">
											{filteredSegmentsDemo?.map(({ title = "", demo = {} }, index) => (
												<Fragment key={index}>
													{(demo?.domain && (
														<li>
															<a
																href={`${demo.domain}/demo`}
																className="dropdown-item text-reset px-4 bg-transparent bg-yellow-200-hover bg-yellow-200-active"
																target="_blank"
																rel="noopener noreferrer">
																<small>
																	<strong>{`for ${title}`}</strong>
																</small>
															</a>
														</li>
													)) || <></>}
												</Fragment>
											))}
										</ul>
									</span>
								</li>
							))}
					</>
				)}
				<li className={`nav-item ${!isVertical ? "col-auto" : ""}`}>
					<LocaleNavLink
						to={`/${!isAuthenticated ? "auth/login" : ""}`}
						className={`btn btn-yellow px-3 px-lg-4 py-2 border-0 w-100 rounded-pill text-uppercase ${
							isVertical ? "fs-5" : ""
						}`}>
						<small>
							<strong>{!isAuthenticated ? t("actions.login_register") : t("actions.dashboard")}</strong>
						</small>
					</LocaleNavLink>
				</li>
			</ul>
		</nav>
	);
};

export default memo(RootHomeNav);
